/**
 * StarterTemplatesSection — curated starter templates grid.
 *
 * Each card has a 3-dot kebab menu:
 *   • Edit   — opens the template editor pre-filled (custom org template)
 *   • Use in Email — saves starter as org template silently, then navigates
 *                    to /emails/compose with that template pre-selected
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, MoreHorizontal, Pencil, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@core/auth/useAuth';
import { emailTemplateService } from '@core/supabase/services/emailTemplateService';
import {
  STARTER_TEMPLATE_CATEGORIES,
  STARTER_TEMPLATES,
  type StarterCategory,
  type StarterTemplate,
} from '../lib/starterTemplates';

// ── Types ────────────────────────────────────────────────────────────────────

interface Props {
  /** Opens the template editor pre-filled with subject + body. */
  onEdit: (subject: string, body: string) => void;
  /** When true, the section starts expanded (used when no org templates exist). */
  defaultExpanded?: boolean;
  /**
   * If provided, "Use in Email" calls this with the saved org-template select
   * value (`org:<id>`) instead of navigating to the full-page compose route.
   */
  onUseInEmail?: (templateSelectValue: string) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<StarterCategory, { bg: string; text: string; dot: string }> = {
  'Candidate Introduction': { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-600 dark:text-blue-400', dot: '#3B82F6' },
  'Client Response': { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-600 dark:text-emerald-400', dot: '#10B981' },
  Interview: { bg: 'bg-violet-50 dark:bg-violet-950/30', text: 'text-violet-600 dark:text-violet-400', dot: '#7C3AED' },
  'Offer & Closure': { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-600 dark:text-amber-400', dot: '#F59E0B' },
  'Follow-up': { bg: 'bg-orange-50 dark:bg-orange-950/30', text: 'text-orange-600 dark:text-orange-400', dot: '#F97316' },
};

function stripHtmlPreview(html: string): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ── StarterCard ───────────────────────────────────────────────────────────────

function StarterCard({
  tpl,
  onEdit,
  onUseInEmail,
  usingId,
}: {
  tpl: StarterTemplate;
  onEdit: () => void;
  onUseInEmail: () => void;
  usingId: string | null;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const preview = stripHtmlPreview(tpl.body_html);
  const color = CATEGORY_COLORS[tpl.category as StarterCategory];
  const isSaving = usingId === tpl.id;

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  return (
    <div
      ref={rootRef}
      className="group relative flex flex-col overflow-hidden rounded-[12px] border transition-all hover:shadow-sm"
      style={{
        background: 'var(--vscode-bg-elevated)',
        borderColor: 'var(--vscode-border-primary)',
      }}
    >
      {/* Header */}
      <div className="flex items-start gap-2.5 px-3.5 pt-3 pb-2">
        <span
          className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px]"
          style={{ background: 'color-mix(in srgb, #10B981 14%, var(--vscode-bg-elevated))', color: '#10B981' }}
          aria-hidden
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M4 6h16v12H4z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            <path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <div className="min-w-0 flex-1 pr-6">
          <p
            className="truncate text-[12.5px] font-semibold leading-snug"
            style={{ color: 'var(--vscode-text-primary)' }}
            title={tpl.name}
          >
            {tpl.name}
          </p>
          <span
            className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${color.bg} ${color.text}`}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: color.dot }} aria-hidden />
            {tpl.category}
          </span>
        </div>
      </div>

      {/* 3-dot kebab menu */}
      <div className="absolute right-2 top-2.5">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
          aria-label="Template actions"
          aria-expanded={menuOpen}
          className="flex h-6 w-6 items-center justify-center rounded-[5px] opacity-0 transition-opacity hover:bg-[var(--vscode-bg-hover)] group-hover:opacity-100 focus:opacity-100"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-7 z-30 w-[160px] overflow-hidden rounded-[8px] border shadow-lg"
            style={{ background: 'var(--vscode-bg-elevated)', borderColor: 'var(--vscode-border-primary)' }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => { setMenuOpen(false); onEdit(); }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12px] hover:bg-[var(--vscode-bg-hover)]"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit template
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => { setMenuOpen(false); onUseInEmail(); }}
              disabled={isSaving}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12px] hover:bg-[var(--vscode-bg-hover)] disabled:opacity-50"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Use in Email
            </button>
          </div>
        )}
      </div>

      {/* Body preview with fade */}
      <div className="relative h-[90px] overflow-hidden px-3.5">
        <p
          className="whitespace-pre-wrap text-[11px] leading-[1.55]"
          style={{ color: 'var(--vscode-text-secondary)' }}
        >
          {preview}
        </p>
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-8"
          style={{ background: 'linear-gradient(to bottom, transparent, var(--vscode-bg-elevated))' }}
        />
      </div>

      {/* Footer */}
      <div
        className="flex items-center justify-between gap-2 border-t px-3.5 py-2"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        <span className="truncate text-[10.5px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
          {tpl.subject.length > 36 ? tpl.subject.slice(0, 36) + '…' : tpl.subject}
        </span>
      </div>
    </div>
  );
}

// ── Main section export ───────────────────────────────────────────────────────

export function StarterTemplatesSection({ onEdit, defaultExpanded = false, onUseInEmail }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<StarterCategory | 'All'>('All');
  const [collapsed, setCollapsed] = useState(!defaultExpanded);
  const [usingId, setUsingId] = useState<string | null>(null);

  const filtered = activeCategory === 'All'
    ? STARTER_TEMPLATES
    : STARTER_TEMPLATES.filter((t) => t.category === activeCategory);

  const handleUseInEmail = async (tpl: StarterTemplate) => {
    if (!user?.orgId || !user.id) return;
    setUsingId(tpl.id);
    try {
      // Save as org template so the compose picker can select it
      const key = `${tpl.id}_${Math.random().toString(36).slice(2, 6)}`;
      const saved = await emailTemplateService.upsert(user.orgId, user.id, {
        key,
        name: tpl.name,
        subject: tpl.subject,
        body_html: tpl.body_html,
        variables: [],
      });
      if (onUseInEmail) {
        // Open inline compose in the parent EmailPage panel
        onUseInEmail(`org:${saved.id}`);
      } else {
        // Fallback for other callers (e.g. standalone route)
        navigate('/emails/compose', {
          state: { templateSelect: key, returnTo: '/emails?etab=templates' },
        });
      }
    } catch {
      if (!onUseInEmail) {
        navigate('/emails/compose', { state: { returnTo: '/emails?etab=templates' } });
      }
    } finally {
      setUsingId(null);
    }
  };

  return (
    <div className={defaultExpanded ? 'mt-2' : 'mt-6'}>
      {/* Section header */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="mb-3 flex w-full items-center gap-2 text-left"
      >
        {collapsed ? (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--vscode-text-tertiary)' }} />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--vscode-text-tertiary)' }} />
        )}
        <span className="text-[12px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
          {defaultExpanded ? 'Pick a starter template' : 'Starter templates'}
        </span>
        <span
          className="ml-1 rounded-full px-1.5 py-0.5 text-[10px]"
          style={{ background: 'var(--vscode-bg-hover)', color: 'var(--vscode-text-tertiary)' }}
        >
          {STARTER_TEMPLATES.length}
        </span>
        <span className="ml-auto text-[11px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
          Ready-to-use templates you can customise
        </span>
      </button>

      {!collapsed && (
        <>
          {/* Category pills */}
          <div className="mb-3 flex flex-wrap gap-1.5">
            {(['All', ...STARTER_TEMPLATE_CATEGORIES] as const).map((cat) => {
              const active = activeCategory === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  className="rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors"
                  style={{
                    borderColor: active ? '#10B981' : 'var(--vscode-border-primary)',
                    background: active ? 'color-mix(in srgb, #10B981 12%, var(--vscode-bg-elevated))' : 'var(--vscode-bg-elevated)',
                    color: active ? '#10B981' : 'var(--vscode-text-secondary)',
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          {/* Grid */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((tpl) => (
              <StarterCard
                key={tpl.id}
                tpl={tpl}
                onEdit={() => onEdit(tpl.subject, tpl.body_html)}
                onUseInEmail={() => handleUseInEmail(tpl)}
                usingId={usingId}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
